import React from "react";

export type ButtonVariant =
  "primary" | "secondary" | "ghost" | "pink" | "on-dark";
export type ButtonSize = "sm" | "md" | "lg";

type ButtonStyleProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
};

/**
 * Returns the className string for a TrueReel button. Exported only so consumers
 * can compose it onto elements that <Button> can't conveniently produce — almost
 * always you want <Button as={Component}> instead. We use it internally too.
 */
export function buttonClass({
  variant = "secondary",
  size = "md",
}: ButtonStyleProps = {}): string {
  const sizeClass =
    size === "lg" ? "tb-btn-lg" : size === "sm" ? "tb-btn-sm" : "";
  return ["tb-btn", `tb-btn-${variant}`, sizeClass].filter(Boolean).join(" ");
}

type AsProp<C extends React.ElementType> = { as?: C };

type PolymorphicProps<C extends React.ElementType, Props = object> = Props &
  AsProp<C> &
  Omit<React.ComponentPropsWithoutRef<C>, keyof (AsProp<C> & Props)>;

type ButtonProps<C extends React.ElementType = "button"> = PolymorphicProps<
  C,
  ButtonStyleProps & {
    className?: string;
    children?: React.ReactNode;
  }
>;

/**
 * TrueReel button. Polymorphic via the `as` prop so the same styling works on
 * `<button>` (default), React Router's `<Link>`, plain `<a>`, or any other
 * element / component that accepts a className.
 *
 *   <Button variant="primary">Save</Button>
 *   <Button as={Link} to="/register" variant="primary">Sign up</Button>
 *   <Button as="a" href="https://docs..." variant="ghost">Docs</Button>
 *
 * Use `as={Link}` for in-app navigation so middle-click / ⌘-click /
 * "open in new tab" / right-click menus all work — never wrap a router
 * navigate() call in an onClick.
 */
export function Button<C extends React.ElementType = "button">({
  as,
  variant,
  size,
  className,
  ...rest
}: ButtonProps<C>) {
  const Component = as ?? "button";
  const cls = [buttonClass({ variant, size }), className]
    .filter(Boolean)
    .join(" ");
  return <Component className={cls} {...rest} />;
}
